// src/utils/printPage.js

/**
 * Fired right before the app prints. Components that hold a long-lived connection (EventSource
 * streams for live updates) close it synchronously on this event and reconnect after printing.
 */
export const PRINT_PAUSE_STREAMS_EVENT = "lumen:print-pause-streams";

/**
 * Prints the current page. Use this instead of window.print().
 *
 * Safari (WebKit) postpones window.print() until the page has finished loading, and an open
 * EventSource keeps the page "loading" forever, so the print dialog would never appear. Open
 * streams are therefore paused first. Call this directly inside the click handler, not from a
 * timer, so it still counts as a user action.
 */
export function printPage() {
  try {
    window.dispatchEvent(new CustomEvent(PRINT_PAUSE_STREAMS_EVENT));
  } catch {
    // printing must still work when a listener fails
  }
  window.print();
}

/**
 * Registers a stream to pause for printing. `pause` closes the connection; `resume` reopens it
 * after the print dialog closes (afterprint, with a timeout as fallback). Returns an unsubscribe.
 */
export function onPrintPauseStreams({ pause, resume, fallbackMs = 10000 }) {
  const handle = () => {
    try {
      pause();
    } catch {
      // ignore
    }
    let resumed = false;
    let timer = null;
    const doResume = () => {
      if (resumed) return;
      resumed = true;
      window.removeEventListener("afterprint", doResume);
      if (timer) window.clearTimeout(timer);
      resume();
    };
    window.addEventListener("afterprint", doResume);
    timer = window.setTimeout(doResume, fallbackMs);
  };
  window.addEventListener(PRINT_PAUSE_STREAMS_EVENT, handle);
  return () => window.removeEventListener(PRINT_PAUSE_STREAMS_EVENT, handle);
}
